// src/services/customBaseQuery.ts
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import type { RootState } from "@/store";
import { auth } from "@/components/auth/firebase";
import { setAuthUser, clearAuthUser } from "@/store/authSlice";
import { addApiMessage } from "@/store/apiMessageSlice";

/**
 * Factory that creates a baseQuery with:
 * - Firebase auth token injection
 * - Auto token refresh on 401
 * - Global API message handling
 */
export const createCustomBaseQuery = (
  baseUrl: string,
  options?: {
    skipAuth?: boolean;   // 👈 NEW
  }
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const rawBaseQuery = fetchBaseQuery({ baseUrl });

  return async (args, api, extraOptions) => {
    const state = api.getState() as RootState;
    const token = state.auth.user?.accessToken;

    let modifiedArgs: FetchArgs =
      typeof args === "string" ? { url: args } : { ...args };

    // ✅ Inject auth ONLY if not skipped
    if (!options?.skipAuth && token) {
      modifiedArgs.headers = {
        ...(modifiedArgs.headers || {}),
        Authorization: `Bearer ${token}`,
      };
    }

    let result = await rawBaseQuery(modifiedArgs, api, extraOptions);

    // 🔁 Retry ONLY if auth is enabled
    if (
      !options?.skipAuth &&
      result.error &&
      result.error.status === 401
    ) {
      try {
        const currentUser = auth.currentUser;

        if (!currentUser) {
          api.dispatch(clearAuthUser());
          return result;
        }

        const newToken = await currentUser.getIdToken(true);
        const currentAuthUser = (api.getState() as RootState).auth.user;

        if (currentAuthUser) {
          api.dispatch(
            setAuthUser({
              ...currentAuthUser,
              accessToken: newToken,
            })
          );
        }

        const retryArgs: FetchArgs = {
          ...modifiedArgs,
          headers: {
            ...(modifiedArgs.headers || {}),
            Authorization: `Bearer ${newToken}`,
          },
        };

        result = await rawBaseQuery(retryArgs, api, extraOptions);
      } catch (err) {
        console.error("Token refresh failed:", err);
        api.dispatch(clearAuthUser());
      }
    }

    // ✅ Global messages (still allowed)
    const data = result?.data as Record<string, any> | undefined;
    if (data?.debug_logs) {
      api.dispatch(addApiMessage(data.debug_logs));
    }

    const errorMessage =
      (result.error?.data as any)?.message ||
      (result.error as any)?.error ||
      null;

    if (errorMessage) {
      api.dispatch(addApiMessage(errorMessage));
    }

    return result;
  };
};
